import isEmail from 'validator/lib/isEmail';
import isMobilePhone from 'validator/lib/isMobilePhone';

import { AnswerSet } from 'src/store/sellerFlow/AnswerSet';

import { checkActiveQuestions } from '.';
import { FlowQuestion, NumberQuestion, NumberRangeQuestion, SelectQuestion } from './FlowQuestion';

type ValidationResultPass = { isValid: true };
type ValidationTooSmallResult = {
  isValid: false;
  type: 'too_small';
  min: number;
};
type ValidationTooLargeResult = {
  isValid: false;
  type: 'too_large';
  max: number;
};

type ValidationRangeResult = {
  isValid: false;
  type: 'min_or_max_invalid';
  min: ValidationResultPass | ValidationTooSmallResult | ValidationTooLargeResult;
  max: ValidationResultPass | ValidationTooSmallResult | ValidationTooLargeResult;
};

export type ValidationResult =
  | ValidationResultPass
  | ValidationRangeResult
  | ValidationTooSmallResult
  | ValidationTooLargeResult
  | { isValid: false; type: 'min_equal_or_greater_than_max' }
  | { isValid: false; type: 'required' }
  | { isValid: false; type: 'unknown_value' }
  | { isValid: false; type: 'email_invalid' }
  | { isValid: false; type: 'phone_invalid' };

type Validator = (question: FlowQuestion, answer: AnswerSet[string]) => ValidationResult;

const validateInRange = (
  value: AnswerSet[string] | number,
  min?: number,
  max?: number,
): ValidationResultPass | ValidationTooSmallResult | ValidationTooLargeResult => {
  if (typeof value === 'undefined') {
    // required validation is separate rule so for undefined we return true here
    return { isValid: true };
  }

  if (typeof max === 'number' && max < value) {
    return { isValid: false, type: 'too_large', max };
  }

  if (typeof min === 'number' && min > value) {
    return { isValid: false, type: 'too_small', min };
  }
  return { isValid: true };
};

const validateMinLessThanMax = (
  minValue?: number | string | boolean,
  maxValue?: number | string | boolean,
): ValidationResult => {
  if (parseInt(`${minValue}`, 10) >= parseInt(`${maxValue}`, 10)) {
    return { isValid: false, type: 'min_equal_or_greater_than_max' };
  }
  return { isValid: true };
};

// these validate specific types of questions, all questions share the same
// code for testing for required/optional, so these only need to do their
// own validation
const validators: Record<string, Validator> = {
  integer: (question: NumberQuestion, answer: AnswerSet[string]): ValidationResult => {
    return validateInRange(answer, question.minValue, question.maxValue);
  },
  dropdown_select: (question: SelectQuestion, answer: AnswerSet[string]): ValidationResult => {
    if (question.options) {
      const foundAnswer = question.options.find((opt) => opt.value == answer);
      if (!foundAnswer) {
        return { isValid: false, type: 'unknown_value' };
      }
    }

    return validateInRange(
      answer,
      question.minValue,
      question.maxValue ? question.maxValue : undefined,
    );
  },
  range: (question: NumberRangeQuestion, answer: AnswerSet[string]): ValidationResult => {
    if (typeof answer == 'object' && 'min' in answer && 'max' in answer) {
      const minInRangeResult = validateInRange(
        parseInt(`${answer.min}`, 10),
        question.minValue,
        question.maxValue,
      );
      const maxInRangeResult = validateInRange(
        parseInt(`${answer.max}`, 10),
        question.minValue,
        question.maxValue,
      );
      if (!minInRangeResult.isValid || !maxInRangeResult.isValid) {
        return {
          isValid: false,
          type: 'min_or_max_invalid',
          min: minInRangeResult,
          max: maxInRangeResult,
        };
      }

      return validateMinLessThanMax(answer.min, answer.max);
    }
    return { isValid: true };
  },
  dropdown_number_range: (_question: FlowQuestion, answer: AnswerSet[string]): ValidationResult => {
    if (typeof answer == 'object' && 'min_price' in answer && 'max_price' in answer) {
      return validateMinLessThanMax(answer.min_price, answer.max_price);
    }
    return { isValid: true };
  },
  email: (_question: FlowQuestion, answer: AnswerSet[string]): ValidationResult => {
    if (typeof answer === 'string' && isEmail(answer)) {
      return { isValid: true };
    }
    return { isValid: false, type: 'email_invalid' };
  },
  phone: (_question: FlowQuestion, answer: AnswerSet[string]): ValidationResult => {
    if (typeof answer === 'string' && isMobilePhone(answer, 'en-US')) {
      return { isValid: true };
    }
    return { isValid: false, type: 'phone_invalid' };
  },
};

/**
 * This works for our current use case, but is not an extremely strict
 * validator, often times it is possible to pass in a compeletely wrong
 * answer and still have the validation pass (for example if you pass {funny:
 * "text"} to a range question it will return true).
 *
 * That is something we should fix in the future through better typing and/or
 * being more serious about validation
 *
 * Making the validation more strict will require a lot of testing of our home
 * details data and historical seller inputs to see what kinds of weird input
 * we might have and how we should approach sanitizing in a nice way for the
 * user
 */
export const checkValidation = (
  question: FlowQuestion,
  answer: AnswerSet[string] | null | undefined,
): ValidationResult => {
  // these types have no actual answers and are always valid (these are things like the overview or agent education ux)
  if (question.type == 'element') {
    return { isValid: true };
  }

  if (answer !== null && answer !== undefined) {
    if (typeof answer !== 'undefined' && answer !== null && answer !== '') {
      // see if this type has its own validation
      const validator = validators[question.type];
      if (validator) {
        return validator(question, answer);
      }

      if (typeof answer === 'string') {
        // if answer is a string, make sure it's not empty after trimming
        // if it is empty, fall through and check if it is optional below
        if (answer.trim() !== '') {
          return { isValid: true };
        }
      } else {
        // we have an answer and no specific validation, so this is question is valid
        return { isValid: true };
      }
    }
  }

  // we have no answer, see if the question is optional
  if (question.optional) {
    return { isValid: true };
  }

  // no answer and not optional so this question is required, and currently invalid
  return { isValid: false, type: 'required' };
};

export type AnswerValidation = {
  valid: Array<string>;
  invalid: Array<{ key: string; result: ValidationResult }>;
  customValidatorErrorMessages: Array<string>;
};
export const checkValidationForActiveQuestions = (
  questions: FlowQuestion[],
  answers: AnswerSet,
): AnswerValidation => {
  const validation: AnswerValidation = { valid: [], invalid: [], customValidatorErrorMessages: [] };
  checkActiveQuestions(
    questions,
    answers,
    (question: FlowQuestion, answer: AnswerSet[string]): boolean => {
      // ignore questions that are hidden
      if (question.hidden) {
        return true;
      }

      const result = checkValidation(question, answer);
      if (result.isValid) {
        validation.valid.push(question.key);
      } else {
        validation.invalid.push({ key: question.key, result });
      }

      // Currently we only have customValidatorErrorMessage on NumberQuestions
      if (question.type == 'integer' && question.customValidatorErrorMessage) {
        validation.customValidatorErrorMessages.push(question.key);
      }

      return true;
    },
  );
  return validation;
};
